import React, { useEffect, useMemo, useRef } from "react";
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  ApolloProvider,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { fromPromise } from "@apollo/client/link/utils";
import * as Sentry from "@sentry/react";
import { useAuthToken, SESSION_TIMED_OUT, USER_TOKEN_NAME, HSA_TOKEN_NAME, PARENT_TOKEN_NAME } from "./useAuthToken";
import { useLocation } from "react-router-dom";
import { isHomeEDApp, apiUrl, isDev } from "@constants";
import { logger } from "../utils/logger";
import { markBackendRequestSent } from "../utils/backendRequestActivity";
import {
  captureGraphQLError,
  captureNetworkError,
} from "../utils/sentryErrorCapture";
import {
  IDP_V2_REFRESH_TOKEN_NAME,
  persistIdpV2Tokens,
  refreshIdpV2Token,
} from "../utils/idpV2Auth";

if (!apiUrl) {
  throw new Error("Data URL not specified.");
}

const httpLink = new HttpLink({ uri: apiUrl });

const cache = new InMemoryCache({
  typePolicies: {
    GroupContent: {
      keyFields: ["contentId", "groupId"],
    },
    AllContent: {
      fields: {
        groups: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    School: {
      fields: {
        courses: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Unit: {
      fields: {
        teacherPreparation: {
          merge(_, incoming) {
            return incoming;
          },
        },
        classPresentation: {
          merge(_, incoming) {
            return incoming;
          },
        },
        studyGuides: {
          merge(_, incoming) {
            return incoming;
          },
        },
        studyGuideMaterials: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Lesson: {
      fields: {
        modules: {
          merge(_, incoming) {
            return incoming;
          },
        },
        materials: {
          merge(_, incoming) {
            return incoming;
          },
        },
        sections: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

const authMiddleware = (getAuthToken: () => string | null) =>
  new ApolloLink((operation, forward) => {
    const authToken = getAuthToken();
    if (authToken) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${authToken}`,
        },
      });
    }

    return forward(operation);
  });

const getUserAuthToken = () => localStorage.getItem(USER_TOKEN_NAME);
const getHsaAuthToken = () => localStorage.getItem(HSA_TOKEN_NAME);
const getParentAuthToken = () => localStorage.getItem(PARENT_TOKEN_NAME);

let idpV2RefreshPromise: Promise<string> | null = null;

function refreshIdpV2AccessToken(setAuthToken: (token: string) => void): Promise<string> {
  const refreshToken = localStorage.getItem(IDP_V2_REFRESH_TOKEN_NAME);
  if (!refreshToken) {
    return Promise.reject(new Error("No IdPv2 refresh token is available"));
  }

  if (!idpV2RefreshPromise) {
    idpV2RefreshPromise = refreshIdpV2Token(refreshToken)
      .then((result) => {
        if (!result.access_token) {
          throw new Error("IdPv2 refresh response did not include an access token");
        }

        persistIdpV2Tokens(result);
        setAuthToken(result.access_token);
        return result.access_token;
      })
      .finally(() => {
        idpV2RefreshPromise = null;
      });
  }

  return idpV2RefreshPromise;
}

const useLatestLogout = () => {
  const { logout } = useAuthToken();
  const logoutRef = useRef(logout);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  return useMemo(() => () => logoutRef.current(), []);
};

const useLatestSetAuthToken = () => {
  const { setAuthToken } = useAuthToken();
  const setAuthTokenRef = useRef(setAuthToken);

  useEffect(() => {
    setAuthTokenRef.current = setAuthToken;
  }, [setAuthToken]);

  return useMemo(() => (token: string) => setAuthTokenRef.current(token), []);
};

const backendActivityLink = new ApolloLink((operation, forward) => {
  markBackendRequestSent();
  return forward(operation);
});

const IDP_ACCESS_TOKEN_INVALID = "IDP_ACCESS_TOKEN_INVALID";
const IDP_VERIFICATION_UNAVAILABLE = "IDP_VERIFICATION_UNAVAILABLE";
const ACCOUNT_EXPIRED = "ACCOUNT_EXPIRED";

const errorLink = (logout: () => void, user?: any, setAuthToken?: (token: string) => void) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    const shouldRefreshIdpV2Token = graphQLErrors?.some(
      ({ extensions }) => extensions?.code === IDP_ACCESS_TOKEN_INVALID,
    );
    const hasRetriedAfterIdpV2Refresh = operation.getContext().idpV2RefreshAttempted;
    const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);

    if (shouldRefreshIdpV2Token && !sessionTimedOut && !hasRetriedAfterIdpV2Refresh && setAuthToken) {
      return fromPromise(
        refreshIdpV2AccessToken(setAuthToken).catch((error) => {
          logger.warn("[Apollo] IdPv2 token refresh failed after auth error", error, {
            component: "Apollo",
            action: "IdPv2RefreshFailed",
          });
          logout();
          throw error;
        }),
      ).flatMap(() => {
        operation.setContext({ idpV2RefreshAttempted: true });
        return forward(operation);
      });
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        // TODO: Remove commenting later - temporarily logging all errors in production for debugging
        // if (isDev) {
        logger.warn(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          null,
          {
            component: "Apollo",
            action: "GraphQLError",
            additionalData: { timestamp: new Date().toISOString() },
          },
        );
        // }

        // Check for session timeout flag before handling auth errors
        const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
        if (sessionTimedOut) {
          logger.warn("[Apollo] Ignoring GraphQL error due to timeout", null, {
            component: "Apollo",
            action: "IgnoreTimeoutError",
            additionalData: {
              errorMessage: message,
              timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
              timestamp: new Date().toISOString(),
            },
          });
          return;
        }

        // Handle auth errors, but exclude certain operations that might return "Not Authorised!" for permission reasons
        const fieldName = path?.[0] ? String(path[0]) : "";
        const declaredOperationName = operation?.operationName
          ? String(operation.operationName)
          : "";
        const operationName = fieldName || declaredOperationName;

        // List of operations that should not trigger logout on "Not Authorised!" errors
        // These are typically permission-based errors rather than authentication errors
        const permissionBasedOperations = [
          "generateAWSKeys",
          "customers",
          "me",
          "userTimeSpent", // Skip logout for time tracking errors
          "heaMe",
          // Groups — used by lesson exclusions UI (ContentAdmin)
          "listGroups",
          // Media uploads used in super-admin lesson/unit edit flows
          "GetUrl",
          "videoUpload",
          "getZIPSlideUrl",
          "uploadZIPSlide",
          // Non-SLL section mutations used in lesson edit sidebar
          "deleteSection",
          "updateSection",
          // SLL — ContentAdmin may get "Not Authorised!" from backend; show error, don't logout
          "sllGetLessons",
          "sllGetGettingStartedLessons",
          "sllGetCaregiverLessons",
          "sllGetLessonsForSA",
          "sllGetLessonInfo",
          "sllGetLessonInfoForSA",
          "sllGetLessonInfoByUniqueName",
          "sllGetLessonExclusions",
          "sllGetCourses",
          "sllGetGroupModules",
          "sllGetBrainBreakLessons",
          "sllGetLastVisitedModule",
          "isValidSLLUniqueName",
          "sllCreateLesson",
          "sllUpdateLesson",
          "sllDeleteLesson",
          "sllAddLessonExclusion",
          "sllRemoveLessonExclusion",
          "sllCreateSection",
          "sllUpdateSection",
          "sllDeleteSection",
          "sllCreateModule",
          "sllUpdateModule",
          "sllDeleteModule",
          "sllUpdateVideoModule",
          "sllMoveModule",
          "sllCreateGroupModule",
          "sllUpdateGroupModule",
          "sllDeleteGroupModule",
          "sllDeleteModuleContent",
          "sllDeleteGroupModuleContent",
          "sllUpdateVideoGroupModule",
          "sllUpdateCourse",
          "sllCreateCourse",
          "sllUpdateCourseName",
          "sllDeleteCourse",
          "sllUpdateLessonCompleteStatus",
          "sllUpdateLastVisitedModule",
        ];

        const isPermissionError =
          message === "Not Authorised!" &&
          permissionBasedOperations.some(
            (name) => name === fieldName || name === declaredOperationName,
          );

        const isIdpVerificationUnavailable = extensions?.code === IDP_VERIFICATION_UNAVAILABLE;
        const isInvalidIdpAccessToken = extensions?.code === IDP_ACCESS_TOKEN_INVALID;
        const isAccountExpired = extensions?.code === ACCOUNT_EXPIRED;

        if (isIdpVerificationUnavailable) {
          logger.warn("[Apollo] Keeping session after retryable IdPv2 verification failure", null, {
            component: "Apollo",
            action: "IdPv2VerificationUnavailable",
            additionalData: { operationName, timestamp: new Date().toISOString() },
          });
        } else if (
          isInvalidIdpAccessToken ||
          isAccountExpired ||
          (message === "Not Authorised!" && !isPermissionError)
        ) {
          logger.warn("[Apollo] Logging out due to auth error", null, {
            component: "Apollo",
            action: "AuthError",
            additionalData: {
              errorMessage: message,
              operationName,
              timestamp: new Date().toISOString(),
            },
          });
          logout?.();
        } else if (isPermissionError) {
          logger.warn("[Apollo] Permission error (not logging out)", null, {
            component: "Apollo",
            action: "PermissionError",
            additionalData: {
              errorMessage: message,
              operationName,
              timestamp: new Date().toISOString(),
            },
          });
          // Don't log out for permission errors on specific operations
        }

        // Filter out noisy GraphQL errors before sending to Sentry
        const noisyGraphQLErrors = [
          "Error while converting text to audio",
          "This survey already expired",
          "SSO_AUTH_FAILED",
          "convertTextToAudio",
          "Most active tab check",
          "Force check running (leader tab)",
          "Force check running",
          "Temporarily disabled to prevent re-render loop",
        ];

        const shouldSkipSentryCapture = noisyGraphQLErrors.some((pattern) =>
          message.toLowerCase().includes(pattern.toLowerCase()),
        );

        // Capture GraphQL errors to Sentry (but not auth/permission errors or noisy errors)
        if (
          !sessionTimedOut &&
          !isInvalidIdpAccessToken &&
          !isAccountExpired &&
          !isIdpVerificationUnavailable &&
          !isPermissionError &&
          !shouldSkipSentryCapture
        ) {
          try {
            // Use centralized GraphQL error capture with user ID
            captureGraphQLError(
              new Error(`GraphQL Error: ${message}`),
              {
                operationName: operation?.operationName,
                variables: operation?.variables,
                query: operation?.query?.loc?.source?.body,
                message,
                locations: locations ? [...locations] : undefined, // Convert readonly array
                path: path ? [...path] : undefined, // Convert readonly array
                extensions,
              },
              user?.id,
            );
          } catch (sentryError) {
            logger.error(
              "[Apollo] Error capturing GraphQL error to Sentry:",
              sentryError,
            );
          }
        }
      });
    }

    if (networkError) {
      // Log network errors with console.warn to ensure they're preserved in production
      logger.warn(`[Apollo] Network error:`, networkError, {
        component: "Apollo",
        action: "NetworkError",
        additionalData: {
          timestamp: new Date().toISOString(),
        },
      });

      // Capture network errors to Sentry
      const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
      if (!sessionTimedOut) {
        try {
          // Use centralized network error capture with user ID
          captureNetworkError(
            networkError,
            {
              operationName: operation?.operationName,
              statusCode: (networkError as any)?.statusCode,
            },
            user?.id,
          );
        } catch (sentryError) {
          logger.error(
            "[Apollo] Error capturing network error to Sentry:",
            sentryError,
          );
        }
      }
    }
  });

// createPersistedQueryLink({
//   sha256,
//   useGETForHashedQueries: true,
// }).concat(new ApolloLink((operation, forward) => {
//   if (authToken) {
//     operation.setContext({
//       headers: {
//         authorization: `Bearer ${authToken}`,
//       },
//     });
//   }

//   return forward(operation);
// }));

export const useAppApolloClient = () => {
  logger.info("[Apollo] useAppApolloClient rendering");
  const logout = useLatestLogout();
  const setAuthToken = useLatestSetAuthToken();

  return useMemo(() => {
    logger.info("[Apollo] Creating new Apollo client");
    return new ApolloClient({
      link: from([
        errorLink(logout, undefined, isHomeEDApp ? undefined : setAuthToken),
        backendActivityLink,
        authMiddleware(isHomeEDApp ? getHsaAuthToken : getUserAuthToken).concat(httpLink),
      ]),
      cache,
    });
  }, [logout, setAuthToken]);
};

export const useParentAppApolloClient = () => {
  const logout = useLatestLogout();

  return useMemo(() => {
    return new ApolloClient({
      link: from([
        errorLink(logout),
        backendActivityLink,
        authMiddleware(getParentAuthToken).concat(httpLink),
      ]),
      cache,
    });
  }, [logout]);
};

export const useHomeSchoolApolloClient = () => {
  const logout = useLatestLogout();

  return useMemo(() => {
    return new ApolloClient({
      link: from([
        errorLink(logout),
        backendActivityLink,
        authMiddleware(getHsaAuthToken).concat(httpLink),
      ]),
      cache,
    });
  }, [logout]);
};

export const AppApolloProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const client = useAppApolloClient();
  const parentClient = useParentAppApolloClient();
  const hsaClient = useHomeSchoolApolloClient();
  const { pathname } = useLocation();

  // Memoize the client selection to prevent unnecessary re-renders
  const selectedClient = useMemo(() => {
    if (pathname.startsWith("/parent")) return parentClient;
    if (pathname.startsWith("/homeschool")) return hsaClient;
    return client;
  }, [pathname, parentClient, hsaClient, client]);

  return <ApolloProvider client={selectedClient}>{children}</ApolloProvider>;
};
